import React, {
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import {
  Button,
  Flex,
  FlexItem
} from "@patternfly/react-core";

import {
  AngleLeftIcon,
  AngleRightIcon
} from "@patternfly/react-icons";

import "./Carousel.css";

const FADE_DURATION = 180;

const Carousel = ({
  items = [],
  getKey,
  renderItem,
  renderItems,
  visibleItems = 1,
  ariaLabel = "Karuzela",
  showCounter = true,
  controlsAlignment = "spread",
  autoPlayInterval = 0,
  pauseOnHover = true,
  pauseOnFocus = true,
  onSlideChange
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocusWithin, setIsFocusWithin] = useState(false);

  const fadeTimeoutRef = useRef(null);

  const onSlideChangeRef = useRef(onSlideChange);

  const safeItems = useMemo(
    () => (Array.isArray(items) ? items : []),
    [items]
  );

  const safeVisibleItems = Math.max(
    1,
    Math.min(visibleItems, safeItems.length || 1)
  );

  const normalizedIndex =
    activeIndex >= safeItems.length
      ? 0
      : activeIndex;

  useEffect(() => {
    onSlideChangeRef.current =
      onSlideChange;
  }, [
    onSlideChange
  ]);

  useEffect(() => {
    const currentItem =
      safeItems[normalizedIndex];

    if (!currentItem) {
      return;
    }

    onSlideChangeRef.current?.(
      normalizedIndex,
      currentItem
    );
  }, [
    normalizedIndex,
    safeItems
  ]);

  const prefersReducedMotion = useMemo(() => {
    return (
      typeof window !== "undefined" &&
      window.matchMedia?.(
        "(prefers-reduced-motion: reduce)"
      ).matches
    );
  }, []);

  const isAutoPlayEnabled =
    Number.isFinite(autoPlayInterval) &&
    autoPlayInterval >= 1000 &&
    safeItems.length > safeVisibleItems &&
    !prefersReducedMotion;

  const isAutoPlayPaused =
    (pauseOnHover && isHovered) ||
    (pauseOnFocus && isFocusWithin);
  
  useEffect(() => {
    if (activeIndex >= safeItems.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, safeItems.length]);

  useEffect(() => {
    return () => {
      if (fadeTimeoutRef.current) {
        window.clearTimeout(fadeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (
      !isAutoPlayEnabled ||
      isAutoPlayPaused ||
      isFading
    ) {
      return undefined;
    }

    const autoPlayTimeout = window.setTimeout(() => {
      setIsFading(true);

      fadeTimeoutRef.current =
        window.setTimeout(() => {
          setActiveIndex((currentIndex) =>
            currentIndex >= safeItems.length - 1
              ? 0
              : currentIndex + 1
          );

          setIsFading(false);
        }, FADE_DURATION);
    }, autoPlayInterval);

    return () => {
      window.clearTimeout(autoPlayTimeout);
    };
  }, [
    autoPlayInterval,
    isAutoPlayEnabled,
    isAutoPlayPaused,
    isFading,
    normalizedIndex,
    safeItems.length
  ]);

  if (safeItems.length === 0) {
    return null;
  }

  const activeItem = safeItems[normalizedIndex];

  const currentItems = Array.from(
    { length: safeVisibleItems },
    (_, offset) =>
      safeItems[
        (normalizedIndex + offset) % safeItems.length
      ]
  );

  const changeSlide = (getNextIndex) => {
    if (isFading) {
      return;
    }

    setIsFading(true);

    fadeTimeoutRef.current = window.setTimeout(() => {
      setActiveIndex((currentIndex) =>
        getNextIndex(currentIndex)
      );

      setIsFading(false);
    }, FADE_DURATION);
  };

  const goToPrevious = () => {
    changeSlide((currentIndex) =>
      currentIndex <= 0
        ? safeItems.length - 1
        : currentIndex - 1
    );
  };

  const goToNext = () => {
    changeSlide((currentIndex) =>
      currentIndex >= safeItems.length - 1
        ? 0
        : currentIndex + 1
    );
  };

  const itemKey = getKey
    ? getKey(activeItem, normalizedIndex)
    : normalizedIndex;

  const hasControls =
    safeItems.length > safeVisibleItems;

  const controls = hasControls ? (
    <Flex
      className={[
        "school-carousel__controls",
        `school-carousel__controls--${controlsAlignment}`
      ].join(" ")}
      alignItems={{
        default: "alignItemsCenter"
      }}
      justifyContent={{
        default:
          controlsAlignment === "center"
            ? "justifyContentCenter"
            : "justifyContentSpaceBetween"
      }}
      gap={{
        default: controlsAlignment === "center"
          ? "gapMd"
          : "gapNone"
      }}
    >
      <FlexItem>
        <Button
          variant="secondary"
          size="md"
          aria-label={`Poprzedni slajd. Aktualnie ${
            normalizedIndex + 1
          } z ${safeItems.length}`}
          icon={<AngleLeftIcon />}
          onClick={goToPrevious}
        />
      </FlexItem>

      {showCounter && (
        <FlexItem>
          <span
            className="school-carousel__counter"
            aria-hidden="true"
          >
            {normalizedIndex + 1} / {safeItems.length}
          </span>
        </FlexItem>
      )}

      <FlexItem>
        <Button
          variant="secondary"
          size="md"
          aria-label={`Następny slajd. Aktualnie ${
            normalizedIndex + 1
          } z ${safeItems.length}`}
          icon={<AngleRightIcon />}
          onClick={goToNext}
        />
      </FlexItem>
    </Flex>
  ) : null;

  const content =
    safeVisibleItems > 1 && renderItems
      ? renderItems(
          currentItems,
          controls,
          normalizedIndex
        )
      : renderItem(
          activeItem,
          normalizedIndex,
          controls
        );

  return (
    <section
      className="school-carousel"
      aria-label={ariaLabel}
      aria-roledescription="karuzela"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocusCapture={() => setIsFocusWithin(true)}
      onBlurCapture={(event) => {
        if (
          !event.currentTarget.contains(
            event.relatedTarget
          )
        ) {
          setIsFocusWithin(false);
        }
      }}
    >
      <div
        className={[
          "school-carousel__slide",
          isFading
            ? "school-carousel__slide--fade-out"
            : "school-carousel__slide--fade-in"
        ].join(" ")}
        key={itemKey}
        role="group"
        aria-roledescription="slajd"
        aria-label={`${
          normalizedIndex + 1
        } z ${safeItems.length}`}
      >
        {content}
      </div>

      {/* <div
        className="school-carousel__status"
        aria-live="polite"
        aria-atomic="true"
      > */}
      <div
        className="school-carousel__status"
        aria-live={
          isAutoPlayEnabled && !isAutoPlayPaused
            ? "off"
            : "polite"
        }
        aria-atomic="true"
      >
        Slajd {normalizedIndex + 1} z {safeItems.length}
      </div>
    </section>
  );
};

export default Carousel;